import { useCallback, useMemo, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { getIdentityVerificationRoute, isCountryNeedsSoftIdAttempt } from '../../../model'
import { getStepOneStatusModel, StepCtaProps, StepStatus, StepOneAction } from './stepOneTaskStatus'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { LaunchDarklyFlags, useFlag, usePortalMigrationEnabled } from '../../../hooks'
import { useNewUserLocalization, isUnsuitabilityRequiredError } from './NewUser.hooks'
import { switchToOldPortal } from '../../../utils/redirects'
import { TrackingEvent } from '../../../model/amplitude'
import { Route } from '../../../config/router'
import { IdentityErrorCode } from '@afterpaytouch/portal-api/identity'
import { ConsumerAccountIdentitiesResponse } from '@afterpaytouch/portal-api/consumer/account/types'
import { Locale } from 'next/dist/compiled/@vercel/og/satori'
import { getCountryCodeFromLocale } from '../../../utils/consumer'
import { getIdentities, useAppDispatch } from '../../../state'
import { IdentityCheckStatus } from '@afterpay/types'

const isRoute = (action: StepOneAction): action is Route => action !== 'unsuitability' && action !== 'idv' && action !== 'none'

interface DynamicArgs {
  creditLimitErrorCode?: IdentityErrorCode | null
  identityCheckStatus: IdentityCheckStatus
}

export interface UseStepOneTaskStatusReturn {
  stepOneStatus: StepStatus
  ctaLoading: boolean
}

export const useStepOneTaskStatus = (args: DynamicArgs): UseStepOneTaskStatusReturn => {
  const { t } = useNewUserLocalization()
  const router = useRouter()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const country = getCountryCodeFromLocale(router.locale as Locale)
  const needSoftIdAttempt = isCountryNeedsSoftIdAttempt(country)
  const dispatch = useAppDispatch()

  const loginMigrationEnabled = usePortalMigrationEnabled()

  const [identitiesData, setIdentitiesData] = useState<ConsumerAccountIdentitiesResponse | undefined>()
  const [identitiesLoading, setIdentitiesLoading] = useState(false)
  const softIdRetryEnabled = useFlag(LaunchDarklyFlags.SoftIdRetryEnabled)

  useEffect(() => {
    let isMounted = true
    // NOTE: Currently we call getIdentities() while the login migration is enabled, and while it needs needSoftIdAttempt(AU, CA, GB region)
    // Because the login and idv will be release TOGETHER in AU
    if (loginMigrationEnabled && needSoftIdAttempt) {
      setIdentitiesLoading(true)
      dispatch(getIdentities())
        .unwrap()
        .then((data) => {
          if (isMounted) {
            setIdentitiesData(data)
          }
        })
        .finally(() => {
          if (isMounted) {
            setIdentitiesLoading(false)
          }
        })
    } else {
      setIdentitiesData(undefined)
      setIdentitiesLoading(false)
    }
    return () => {
      isMounted = false
    }
  }, [loginMigrationEnabled, needSoftIdAttempt])

  const redirectToUnsuitabilityFlow = useCallback((): void => {
    router.push(Route.UNSUITABILITY_QUESTION_FORM)
    logEvent(TrackingEvent.CLICKED_COMPLETE_NOW)
  }, [router, logEvent])

  const push = useCallback(
    (url: string): void => {
      void router.push(url)
    },
    [router]
  )

  const model = useMemo(
    () =>
      getStepOneStatusModel({
        creditLimitErrorCode: args.creditLimitErrorCode,
        isUnsuitabilityRequiredError,
        identityCheckStatus: args.identityCheckStatus,
        country,
        identitiesData,
      }),
    [args.creditLimitErrorCode, country, identitiesData, args.identityCheckStatus]
  )

  const base: StepStatus = useMemo(
    () => ({
      task: t(model.taskKey),
      subtask: t(model.subtaskKey),
      isCompleted: model.isCompleted,
      cta: { label: t('index:welcome:steps:verify:task-not-required'), onClick: () => {} },
    }),
    [model.isCompleted, model.subtaskKey, model.taskKey, t]
  )

  const makeIdvCta = useCallback<() => StepCtaProps>(
    () => ({
      label: t('index:welcome:steps:verify:cta:label'),
      onClick: () => {
        logEvent(TrackingEvent.CLICKED_VERIFY_NOW)
        try {
          if (loginMigrationEnabled) {
            const route = getIdentityVerificationRoute(softIdRetryEnabled, needSoftIdAttempt, identitiesData, country)
            push(route)
          } else {
            switchToOldPortal('login-finished')
          }
        } catch (error) {
          logEvent(TrackingEvent.CLICKED_VERIFY_NOW_ERROR, { error })
        }
      },
    }),
    [loginMigrationEnabled, needSoftIdAttempt, identitiesData, country, push, logEvent, t]
  )

  const stepOneStatus: StepStatus = useMemo(() => {
    if (model.logEvent != null) {
      logEvent(model.logEvent.event as any, model.logEvent.payload)
    }
    if (model.action === 'unsuitability') {
      return { ...base, cta: { label: t('index:welcome:steps:unsuitability:cta:label'), onClick: () => redirectToUnsuitabilityFlow() } }
    }
    if (isRoute(model.action)) {
      return {
        ...base,
        cta: { label: t('index:welcome:steps:verify:cta:failedLabel'), onClick: () => push(model.action) },
      }
    }
    if (model.action === 'idv') {
      return { ...base, cta: makeIdvCta() }
    }
    return base
  }, [base, makeIdvCta, model, t, push, redirectToUnsuitabilityFlow, logEvent])

  return { stepOneStatus, ctaLoading: identitiesLoading }
}
